import { DataTableColumn } from '../../../shared/ui/data-table/data-table.component';

/**
 * The columns of the Roll-off Hires list, in display order.
 *
 * Written beside LEAD_LIST_COLUMNS and to the same contract, deliberately: the
 * data table, the column picker and the stored preferences are one piece of
 * machinery, and a second table built by hand beside it is the drift this
 * repository has already paid for once (see README, "A Service Line Is Data").
 * What differs is the LIST of columns, which is the part that genuinely
 * differs - a hire has a site, a container, a price and two dates where a lead
 * has sources, a score and a route fit.
 *
 * `sortable: false` is not decoration. It marks the columns the server has no
 * allowlisted sort for, and the allowlist is in RollOffOpportunityRepository -
 * a header offering an order the server will not honour is a click that
 * silently does nothing.
 *
 * Ids are the query contract: `sortKey` is what the server sorts on, and the
 * ids double as the keys the page's `dtCell` templates and the column picker's
 * stored preferences are written against. Renaming one resets that user's
 * choice, which is why the labels move and the ids do not.
 */
export const ROLLOFF_LIST_COLUMNS: readonly DataTableColumn[] = [
  { id: 'siteName', label: 'Site' },
  { id: 'address', label: 'Address', sortable: false },
  { id: 'city', label: 'City' },
  { id: 'postalCode', label: 'ZIP', sortable: false },
  { id: 'stage', label: 'Stage', sortKey: 'stageEnteredAt' },
  { id: 'container', label: 'Container', sortable: false },
  { id: 'quotedPrice', label: 'Quoted' },
  { id: 'hireStartsOn', label: 'Starts' },
  { id: 'hireEndsOn', label: 'Ends' },
  { id: 'salesRep', label: 'Sales rep', sortable: false },
  { id: 'createdAt', label: 'Created' },
];

/**
 * What a hire's container reads as.
 *
 * The label is the tenant's own word for the type; the free-text size is what
 * was written before types existed and on hires whose type was never set. One
 * of them, never both - printing "8 yd (8 yard skip)" says the same thing
 * twice and is how a column that fits becomes a column that wraps.
 */
export function rollOffContainerLabel(hire: {
  containerTypeLabel?: string | null;
  containerSizeText?: string | null;
}): string {
  return hire.containerTypeLabel || hire.containerSizeText || '—';
}

/**
 * A hire whose end date has passed while it is still open.
 *
 * The same shape of question the leads table asks of an overdue next action,
 * and marked the same way, because it is the same glance: something that
 * should have moved and did not.
 */
export function rollOffHireOverdue(
  endsOn: string | null | undefined,
  outcomeAt: string | null | undefined,
): boolean {
  if (outcomeAt != null) return false;
  return endsOn != null && new Date(endsOn) < new Date();
}
